import React, { RefObject, useRef, useState } from "react";
import { TextInputField, TextInputFieldProps, Pane } from "evergreen-ui";

import './styles/PrefixInputField.css';
import { ColorPalette } from "../context/Theme";

interface PrefixInputFieldProps extends Omit<TextInputFieldProps, "inputWidth"> {
    suffix: React.ReactNode;
}

const HORIZONTAL_INSET: number = 12; // In pixels

export const PrefixInputField: React.FC<PrefixInputFieldProps> = ({ suffix, ...props }) => {
    const suffixRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const inputRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    const [suffixPaneBottom, setSuffixPaneBottom] = useState<number>(0);
    const [inputFieldPaddingRight, setInputFieldPaddingRight] = useState<number>(HORIZONTAL_INSET);

    React.useEffect(() => {
        if (suffixRef.current && containerRef.current && inputRef.current) {
            const containerRect: DOMRect = containerRef.current.getBoundingClientRect();
            const inputRect: DOMRect = inputRef.current.getBoundingClientRect();

            // The suffix pane is bottom-aligned with the input field.
            setSuffixPaneBottom(containerRect.bottom - inputRect.bottom);

            // This padding prevents the actual text area from overlapping with the suffix pane.
            setInputFieldPaddingRight(suffixRef.current.offsetWidth + HORIZONTAL_INSET);
        }
    }, [suffix]);

    return (
        <Pane
            className={"prefix-input-field-container"}
            ref={containerRef}
            width={props.width}
            maxWidth={props.maxWidth}
            minWidth={props.minWidth}
        >
            <TextInputField
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                ref={inputRef}
                {...props}
                width={"100%"}  // The input field must span the full width of its container.
                style={{
                    paddingRight: inputFieldPaddingRight,
                    color: props.disabled ? ColorPalette.lightGreyText : "default",
                    backgroundColor: props.disabled ? "transparent" : 'default'
                }}
            />
            <Pane
                className={"prefix-input-field-suffix-pane"}
                ref={suffixRef}
                right={HORIZONTAL_INSET}
                bottom={suffixPaneBottom}
                height={inputRef.current?.offsetHeight ?? "auto"}
            >
                {suffix}
            </Pane>
        </Pane>
    );
};
